import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { ReactComponent as IconStepSuccess } from '../../../assets/img/step-success.svg';
import { ReactComponent as LinkedInIcon } from '../../../assets/img/icn-linkedin.svg';
import { LeadDeltaOnboardingIcon, OnboardingConnectionIconRow, OnboardingLinkIcon } from './OnboardingConnectionIcons';
import LinkedInProfileAvatar from './LinkedInProfileAvatar';
import { styles } from '../../../styles/themes/style';
import { ILinkedInUserData } from '../../../interfaces/models/user';
import {
    AuthOnboardingCard,
    AuthOnboardingCardBody,
    AuthOnboardingIntro,
    AuthMethodButton,
    AuthSkipLink,
    AuthInfoBox,
} from '../../authentication/authFormCommon';

export const LINKEDIN_CONNECT_PAGE_SUBTITLE =
    "We found the LinkedIn profile linked to this browser. Confirm it's you so LeadDelta syncs the right network.";

export const LINKEDIN_CONNECT_CARD_INTRO =
    'LeadDelta needs to know which LinkedIn profile to connect to your workspace. This helps keep your contacts, inbox, and network data linked to the right account.';

export interface IConnectLinkedInProfileCardProps {
    userLI?: ILinkedInUserData;
    statusMessage: string;
    loading: boolean;
    connecting: boolean;
    showProfile: boolean;
    onConnect: () => void;
    onRefresh: () => void;
    onUseDifferentProfile: () => void;
}

const ConnectLinkedInProfileCard: FunctionComponent<IConnectLinkedInProfileCardProps> = ({
    userLI,
    statusMessage,
    loading,
    connecting,
    showProfile,
    onConnect,
    onRefresh,
    onUseDifferentProfile,
}) => {
    const linkedinUrl =
        userLI?.linkedinUrl ||
        (userLI?.publicIdentifier
            ? `https://www.linkedin.com/in/${userLI.publicIdentifier}`
            : '');

    return (
        <AuthOnboardingCard>
        <OnboardingConnectionIconRow>
            <LeadDeltaOnboardingIcon aria-hidden />
            <OnboardingLinkIcon />
            <LinkedInBrandIcon aria-hidden>
                <LinkedInIcon />
            </LinkedInBrandIcon>
        </OnboardingConnectionIconRow>

        <AuthOnboardingCardBody>
            <AuthOnboardingIntro>{LINKEDIN_CONNECT_CARD_INTRO}</AuthOnboardingIntro>

            {!!statusMessage && (
                <AuthInfoBox>
                    <p>{statusMessage}</p>
                </AuthInfoBox>
            )}

            {showProfile && userLI && (
                <ProfileSection>
                    <ProfileSectionLabel>Is this you?</ProfileSectionLabel>
                    <ProfileCard>
                        <ProfileCardLeft>
                            <LinkedInProfileAvatar
                                profilePicture={userLI.profilePicture}
                                capitalLetters={`${userLI.firstName?.charAt(0) ?? ''}${userLI.lastName?.charAt(0) ?? ''}`}
                            />
                            <ProfileDetails>
                                <ProfileName>
                                    {`${userLI.firstName} ${userLI.lastName}`}
                                </ProfileName>
                                {linkedinUrl && <ProfileLinkedInUrl>{linkedinUrl}</ProfileLinkedInUrl>}
                            </ProfileDetails>
                        </ProfileCardLeft>
                        <IconStepSuccess aria-hidden />
                    </ProfileCard>
                </ProfileSection>
            )}

            {showProfile && (
                <CardActions>
                    <AuthMethodButton
                        $type="blue"
                        disabled={loading || connecting}
                        isLoading={connecting}
                        loadingText="Connecting..."
                        tooltipMessage=""
                        onClickHandler={onConnect}
                    >
                        Connect LinkedIn profile
                    </AuthMethodButton>
                    <AuthSkipLink type="button" disabled={connecting} onClick={onUseDifferentProfile}>
                        Use a different LinkedIn profile
                    </AuthSkipLink>
                </CardActions>
            )}

            {!showProfile && (
                <CardActions>
                    <AuthMethodButton
                        $type="grey"
                        $SVGtype="reload"
                        disabled={loading}
                        isLoading={loading}
                        loadingText="Checking..."
                        tooltipMessage=""
                        onClickHandler={onRefresh}
                    >
                        Refresh LinkedIn profile
                    </AuthMethodButton>
                </CardActions>
            )}
        </AuthOnboardingCardBody>
        </AuthOnboardingCard>
    );
};

const LinkedInBrandIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    flex-shrink: 0;
    border-radius: 8px;

    svg {
        width: 60px;
        height: 60px;
    }
`;

const ProfileSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: 100%;
`;

const ProfileSectionLabel = styled.p`
    margin: 0;
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    color: ${styles.colors.black400};
`;

const ProfileCard = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    box-sizing: border-box;
    background: ${styles.colors.primary100};
    border: 1px solid ${styles.colors.primary200};
    border-radius: 8px;
`;

const ProfileCardLeft = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
`;

const ProfileDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const ProfileName = styled.span`
    font-family: NunitoSans;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
`;

const ProfileLinkedInUrl = styled.span`
    font-family: NunitoSans;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black400};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CardActions = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    width: 100%;
`;

export default ConnectLinkedInProfileCard;
